const ATTRIBUTION_KEY = 'mp_signup_attribution';
const REFERRAL_KEY = 'mp_referral';
const CONSENT_KEY = 'mp_marketing_consent';
const IDENTITY_KEY = 'mp_marketing_identity';
const EVENT_QUEUE_KEY = 'mp_marketing_event_queue';
const SESSION_KEY = 'mp_marketing_session';
const CONSENT_VERSION = '2026-09-07';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180;
const EVENT_ENDPOINT = 'https://message-pro.com/api/marketing/events';
const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_ad_id',
  'utm_adset_id',
  'utm_placement',
  'utm_term',
  'utm_content',
  'utm_source_platform',
  'utm_marketing_tactic',
  'utm_creative_format',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'fbclid',
  'ttclid',
  'msclkid',
  'twclid',
  'li_fat_id',
  'ref',
];
const META_STANDARD_EVENTS = {
  page_view: 'PageView',
  signup_view: 'ViewContent',
  signup_start: 'InitiateCheckout',
  sign_up: 'CompleteRegistration',
};
const META_CUSTOM_EVENTS = {
  onboarding_complete: 'OnboardingComplete',
};

const truncate = (value, limit = 128) =>
  typeof value === 'string' ? value.trim().slice(0, limit) : '';

const normalizeReferralCode = value => {
  const code = truncate(value, 50).toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{2,49}$/.test(code) ? code : '';
};

const safeParse = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const cookieDomain = () => {
  const isMessageProDomain =
    window.location.hostname === 'message-pro.com' ||
    window.location.hostname.endsWith('.message-pro.com');
  return isMessageProDomain ? '; Domain=.message-pro.com; Secure' : '';
};

const writeCookie = (key, value, maxAge) => {
  document.cookie = `${key}=${encodeURIComponent(JSON.stringify(value))}; Path=/; Max-Age=${maxAge}; SameSite=Lax${cookieDomain()}`;
};

const removeCookie = key => {
  document.cookie = `${key}=; Path=/; Max-Age=0; SameSite=Lax`;
  document.cookie = `${key}=; Path=/; Max-Age=0; SameSite=Lax${cookieDomain()}`;
};

const createId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const configureGoogleConsent = consent => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
  window.gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.advertising ? 'granted' : 'denied',
    ad_user_data: consent.advertising ? 'granted' : 'denied',
    ad_personalization: consent.advertising ? 'granted' : 'denied',
  });
};

const loadGoogleTagManager = containerId => {
  if (!containerId || document.getElementById('message-pro-signup-gtm')) return;

  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = 'message-pro-signup-gtm';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
};

const loadGoogleAdsTag = tagId => {
  if (!tagId || document.getElementById('message-pro-signup-google-ads')) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'message-pro-signup-google-ads';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  window.gtag('config', tagId);
};

const loadMetaPixel = pixelId => {
  if (!pixelId || window.fbq) return;

  const fbq = function metaPixel() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
    } else {
      fbq.queue.push(arguments);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', pixelId);
};

const advertisingEvent = (event, consent) => {
  const config = window.chatwootConfig || {};
  const parameters = { ...event.properties, event_id: event.event_id };

  configureGoogleConsent(consent);
  if (consent.analytics) {
    loadGoogleTagManager(config.marketingGoogleTagManagerId);
    window.dataLayer.push({ event: event.event_name, ...parameters });
  }

  if (!consent.advertising) return;

  loadGoogleAdsTag(config.marketingGoogleAdsTagId);
  loadMetaPixel(config.marketingMetaPixelId);

  const googleLabel = {
    generate_lead: config.marketingGoogleAdsLeadLabel,
    sign_up: config.marketingGoogleAdsSignupLabel,
  }[event.event_name];
  if (config.marketingGoogleAdsTagId && googleLabel) {
    window.gtag('event', 'conversion', {
      send_to: `${config.marketingGoogleAdsTagId}/${googleLabel}`,
      transaction_id: event.event_id,
    });
  }

  if (!window.fbq) return;

  const metaEventName = META_STANDARD_EVENTS[event.event_name];
  window.fbq(
    metaEventName ? 'track' : 'trackCustom',
    metaEventName || META_CUSTOM_EVENTS[event.event_name] || event.event_name,
    parameters,
    { eventID: event.event_id }
  );
};

const deviceCategory = () => {
  if (window.innerWidth < 640) return 'mobile';
  if (window.innerWidth < 1024) return 'tablet';

  return 'desktop';
};

const decodePayload = value => {
  if (!value) return null;

  try {
    const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
    const binary = window.atob(base64);
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
    return safeParse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
};

const readCookie = key => {
  const prefix = `${key}=`;
  const entry = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith(prefix));

  return entry
    ? safeParse(decodeURIComponent(entry.slice(prefix.length)))
    : null;
};

const readCookieValue = key => {
  const prefix = `${key}=`;
  const entry = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : '';
};

const googleSessionId = () => {
  // This public stream ID also identifies the GA4 cookie on message-pro.com.
  const cookie = readCookieValue('_ga_FM5DWDDE16');
  const value = cookie.startsWith('GS2.')
    ? cookie.match(/(?:^|[.$])s(\d+)/)?.[1]
    : cookie.split('.')[2];
  return /^[1-9]\d{0,12}$/.test(value || '') ? value : undefined;
};

const advertisingIdentifiers = (signupPayload, fallbackClientId) => {
  const googleCookie = readCookieValue('_ga');
  return {
    ga_client_id:
      signupPayload?.ga_client_id ||
      googleCookie.split('.').slice(-2).join('.') ||
      fallbackClientId,
    ga_session_id: googleSessionId() || signupPayload?.ga_session_id,
    fbp: signupPayload?.fbp || readCookieValue('_fbp') || undefined,
    fbc: signupPayload?.fbc || readCookieValue('_fbc') || undefined,
  };
};

const readStorage = key => {
  try {
    return safeParse(window.localStorage.getItem(key));
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Cookies remain the fallback when storage is unavailable.
  }
};

const removeStorage = key => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing else is required when storage is unavailable.
  }
};

const readSignupPayload = () => {
  const query = new URLSearchParams(window.location.search);
  return decodePayload(query.get('signup_attribution'));
};

const pricingPreference = signupPayload => {
  const query = new URLSearchParams(window.location.search);
  const raw = signupPayload?.pricing_preference || signupPayload || {};
  const planSlug = truncate(
    raw.preferred_plan_slug || query.get('preferred_plan_slug'),
    32
  ).toLowerCase();
  const billingPeriodDays = Number.parseInt(
    raw.preferred_billing_period_days ||
      query.get('preferred_billing_period_days'),
    10
  );
  const countryCode = truncate(
    raw.pricing_country_code || query.get('pricing_country_code'),
    2
  ).toUpperCase();
  const preference = {
    preferred_plan_slug: ['starter', 'growth', 'advanced'].includes(planSlug)
      ? planSlug
      : undefined,
    preferred_billing_period_days: [30, 180, 365].includes(billingPeriodDays)
      ? billingPeriodDays
      : undefined,
    pricing_country_code: /^[A-Z]{2}$/.test(countryCode)
      ? countryCode
      : undefined,
  };

  return Object.values(preference).some(Boolean) ? preference : null;
};

const referrerDetails = () => {
  if (!document.referrer) return {};

  try {
    const referrer = new URL(document.referrer);
    const isInternal =
      referrer.hostname === 'message-pro.com' ||
      referrer.hostname.endsWith('.message-pro.com');
    if (isInternal) return {};

    return {
      referrer: truncate(referrer.origin, 500),
      referrer_domain: truncate(referrer.hostname),
    };
  } catch {
    return {};
  }
};

const currentTouch = () => {
  const query = new URLSearchParams(window.location.search);
  const campaign = Object.fromEntries(
    CAMPAIGN_KEYS.map(key => [key, truncate(query.get(key))]).filter(
      ([, value]) => value
    )
  );
  const referrer = referrerDetails();

  return {
    ...campaign,
    ...referrer,
    source:
      campaign.utm_source ||
      (campaign.ref ? 'affiliate' : referrer.referrer_domain) ||
      '(direct)',
    medium:
      campaign.utm_medium ||
      (campaign.ref || referrer.referrer_domain ? 'referral' : '(none)'),
    landing_page: truncate(
      `${window.location.origin}${window.location.pathname}`,
      500
    ),
    locale: truncate(document.documentElement.lang, 16),
    captured_at: new Date().toISOString(),
  };
};

const writeAttribution = attribution => {
  writeStorage(ATTRIBUTION_KEY, attribution);
  writeCookie(ATTRIBUTION_KEY, attribution, COOKIE_MAX_AGE);
};

const sessionId = preferred => {
  try {
    const current = preferred || window.sessionStorage.getItem(SESSION_KEY);
    const value = current || createId();
    window.sessionStorage.setItem(SESSION_KEY, value);
    return value;
  } catch {
    return preferred || createId();
  }
};

const identity = signupPayload => {
  const stored = readStorage(IDENTITY_KEY) || readCookie(IDENTITY_KEY) || {};
  const value = {
    visitor_id: signupPayload?.visitor_id || stored.visitor_id || createId(),
    session_id: sessionId(signupPayload?.session_id),
  };
  writeStorage(IDENTITY_KEY, { visitor_id: value.visitor_id });
  writeCookie(IDENTITY_KEY, { visitor_id: value.visitor_id }, COOKIE_MAX_AGE);
  return value;
};

const captureReferral = signupPayload => {
  const query = new URLSearchParams(window.location.search);
  const existing = readStorage(REFERRAL_KEY) || readCookie(REFERRAL_KEY);
  const storedAttribution =
    readStorage(ATTRIBUTION_KEY) || readCookie(ATTRIBUTION_KEY);
  const code = normalizeReferralCode(
    signupPayload?.referral_code ||
      query.get('ref') ||
      existing?.code ||
      storedAttribution?.referral_code
  );
  if (!code) return null;

  const referral = {
    code,
    landing_page: truncate(
      signupPayload?.first_touch?.landing_page ||
        existing?.landing_page ||
        `${window.location.origin}${window.location.pathname}`,
      500
    ),
    captured_at:
      signupPayload?.first_touch?.captured_at ||
      existing?.captured_at ||
      new Date().toISOString(),
  };
  writeStorage(REFERRAL_KEY, referral);
  writeCookie(REFERRAL_KEY, referral, COOKIE_MAX_AGE);
  return referral;
};

export const isMessageProMarketingHost = () =>
  ['app.message-pro.com', 'omni.message-pro.com'].includes(
    window.location.hostname
  );

export const readMarketingConsent = () => readCookie(CONSENT_KEY);

export const saveMarketingConsent = ({
  analytics,
  advertising,
  source = 'explicit_choice',
}) => {
  const consent = {
    analytics,
    advertising,
    source,
    version: CONSENT_VERSION,
    updated_at: new Date().toISOString(),
  };

  if (!analytics) {
    [ATTRIBUTION_KEY, IDENTITY_KEY, EVENT_QUEUE_KEY].forEach(key => {
      removeStorage(key);
      removeCookie(key);
    });
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // No session cleanup is required when storage is unavailable.
    }
  }

  writeCookie(CONSENT_KEY, consent, CONSENT_MAX_AGE);
  configureGoogleConsent(consent);
  window.fbq?.('consent', advertising ? 'grant' : 'revoke');
  return consent;
};

export const captureSignupAttribution = () => {
  const signupPayload = readSignupPayload();
  // A current browser choice overrides consent embedded in an old signup URL.
  const consent = readCookie(CONSENT_KEY) || signupPayload?.consent || {};
  const referralCode = captureReferral(signupPayload)?.code || '';
  const selectedPricing = pricingPreference(signupPayload);
  if (consent.analytics !== true) {
    if (!referralCode && !selectedPricing) return {};

    const referralAttribution = {
      referral_code: referralCode,
      pricing_preference: selectedPricing,
      consent,
    };
    writeAttribution(referralAttribution);
    return referralAttribution;
  }

  const stored =
    signupPayload ||
    readStorage(ATTRIBUTION_KEY) ||
    readCookie(ATTRIBUTION_KEY);
  const touch = currentTouch();
  const hasCampaignSignal =
    CAMPAIGN_KEYS.some(key => touch[key]) || Boolean(touch.referrer_domain);
  const touches = stored
    ? {
        first_touch: stored.first_touch || touch,
        last_touch: hasCampaignSignal
          ? touch
          : stored.last_touch || stored.first_touch || touch,
      }
    : { first_touch: touch, last_touch: touch };
  const attribution = {
    ...touches,
    referral_code: referralCode || stored?.referral_code,
    pricing_preference: selectedPricing || stored?.pricing_preference,
  };
  const marketingIdentity = identity(signupPayload);

  const completeAttribution = {
    ...attribution,
    ...marketingIdentity,
    ...advertisingIdentifiers(
      signupPayload || stored,
      marketingIdentity.visitor_id
    ),
    consent,
  };
  if (consent.advertising !== true) {
    delete completeAttribution.fbp;
    delete completeAttribution.fbc;
  }
  // OAuth callbacks read this cookie instead of the email-signup request body.
  writeAttribution(completeAttribution);
  return completeAttribution;
};

const eventQueue = () => readStorage(EVENT_QUEUE_KEY) || [];

const queueEvent = event => {
  writeStorage(EVENT_QUEUE_KEY, [...eventQueue(), event].slice(-50));
};

const deliverEvents = async events => {
  if (!events.length) return;

  try {
    const endpoint =
      window.chatwootConfig?.marketingEventsUrl || EVENT_ENDPOINT;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events }),
      keepalive: true,
    });
    if (!response.ok) throw new Error('Marketing event delivery failed');
  } catch {
    if (readMarketingConsent()?.analytics === false) return;

    events.forEach(queueEvent);
  }
};

export const flushSignupJourneyEvents = async () => {
  if (captureSignupAttribution().consent?.analytics !== true) {
    removeStorage(EVENT_QUEUE_KEY);
    return;
  }
  const events = eventQueue();
  if (!events.length) return;

  writeStorage(EVENT_QUEUE_KEY, []);
  await deliverEvents(events);
};

export const trackSignupJourneyEvent = (
  eventName,
  properties = {},
  { eventId } = {}
) => {
  const attribution = captureSignupAttribution();
  if (!attribution.consent?.analytics) return;

  const event = {
    event_id: eventId || createId(),
    event_name: eventName,
    visitor_id: attribution.visitor_id,
    session_id: attribution.session_id,
    ga_client_id: attribution.ga_client_id,
    ga_session_id: attribution.ga_session_id,
    fbp: attribution.fbp,
    fbc: attribution.fbc,
    occurred_at: new Date().toISOString(),
    page_path: truncate(window.location.pathname, 500),
    page_title: truncate(document.title, 255),
    country_code:
      truncate(window.chatwootConfig?.marketingCountryCode, 2).toUpperCase() ||
      undefined,
    locale: truncate(document.documentElement.lang, 16),
    device_category: deviceCategory(),
    properties,
    attribution: {
      first_touch: attribution.first_touch,
      last_touch: attribution.last_touch,
      referral_code: attribution.referral_code,
    },
    consent: attribution.consent,
  };

  advertisingEvent(event, attribution.consent);
  deliverEvents([event]);
};
